import React, { useCallback, useMemo, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Box, Button, Grid, IconButton } from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import EditIcon from "@mui/icons-material/Edit";

import { getOptionLabel } from "common/enums";
import BreadCrumb from "Components/Common/BreadCrumb";
import BaseTable from "Components/Common/BaseTable";
import Tooltip from "Components/Common/Tooltip";
import InputFieldText from "Components/Common/InputFieldText";
import ConsumerNumbersDisplay from "./ConsumerNumbersDisplay";
import AddOrEditAuthorizedPerson from "./AddOrEditAuthorizedPerson";
import ViewUserDetails from "./ViewUserDetails";

import { AUTHORIZED_USERS,  } from "tagTypes";
import { onlyNumberKeys } from "utils";

const RECORDS_PER_PAGE = 10;

const AuthorizedUsers = ({ t }) => {
  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);
  const [authorizedPersonEdit, setAuthorizedPersonEdit] = useState(null);
  const [authorizedPersonView, setAuthorizedPersonView] = useState(null);
  const [consumerNumber, setConsumerNumber] = useState("");
  const [searchParams, setSearchParams] = useState("");
  const [query, setQuery] = useState({});

  const resetPageRef = useRef();

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const columns = useMemo(
    () => [
      {
        title: "Name",
        renderCell: ({ salutation, firstName, lastName = "" }) =>
          `${getOptionLabel("salutationOptions", salutation)} ${firstName} ${
            lastName ?? ""
          }`,
      },
      {
        title: "Designation",
        field: "designation",
      },
      {
        title: "Email",
        field: "email",
      },
      {
        title: "Mobile Number",
        field: "mobileNumber",
      },
      {
        title: "Consumer Numbers",
        renderCell: ({ authorizedUserConsumers }) => (
          <ConsumerNumbersDisplay consumerNumbers={authorizedUserConsumers} />
        ),
        columnProps: { style: { width: "25%" } },
      },
      {
        title: "Actions",
        renderCell: (authorizedPersonDetails) => (
          <Box sx={{ minWidth: "100px" }}>
            <Tooltip title={t("View User Details")}>
              <IconButton
                onClick={() => setAuthorizedPersonView(authorizedPersonDetails)}
              >
                <RemoveRedEyeIcon fontSize="small" color="primary" />
              </IconButton>
            </Tooltip>

            <Tooltip title={t("Edit User Details")}>
              <IconButton
                onClick={() => setAuthorizedPersonEdit(authorizedPersonDetails)}
              >
                <EditIcon fontSize="small" color="primary" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    []
  );

  const clearBtnHandler = () => {
    handleResetPage();
    setConsumerNumber("");
    setSearchParams("");
    setQuery({});
  };

  const serverSidePaginationConfig = useMemo(
    () => ({
      endpoint: "/user/authorized-user",
      tags: [AUTHORIZED_USERS],
      query,
    }),
    [query]
  );

  return (
    <div className="page-content h-100">
      <BreadCrumb title={t("Authorized Users")} pageTitle={t("User")} />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleResetPage();
          setQuery({ consumerNumber, searchParams });
        }}
      >
        <Grid container columnSpacing={2} className="mt-2 mb-2">
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputFieldText
              className="w-100"
              label={t("Enter Consumer Number")}
              type="number"
              onChange={(e) => setConsumerNumber(e.target.value)}
              placeholder="Enter Consumer Number"
              value={consumerNumber}
              InputProps={{
                inputProps: { min: 0, onKeyDown: onlyNumberKeys },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputFieldText
              className="w-100"
              label={t("Search Here")}
              onChange={(e) => setSearchParams(e.target.value)}
              placeholder="Search Here"
              value={searchParams}
            />
          </Grid>
          <Grid item>
            <Button variant="outlined" type="submit" className="mb-2 me-2">
              {t("Search")}
            </Button>
            <Button
              variant="outlined"
              className="mb-2 me-2"
              onClick={clearBtnHandler}
            >
              {t("Clear")}
            </Button>
            <Button
              variant="contained"
              className="mb-2 me-2"
              onClick={() => setAuthorizedPersonEdit(true)}
            >
              {t("Add Authorized User")}
            </Button>
          </Grid>
        </Grid>
      </form>
      {authorizedPersonView && (
        <ViewUserDetails
          userId={authorizedPersonView.id}
          handleClose={() => setAuthorizedPersonView(null)}
          isViewingFromAuthorizedUsersPage
        />
      )}

      {authorizedPersonEdit && (
        <AddOrEditAuthorizedPerson
          authorizedPersonUserId={authorizedPersonEdit.id}
          handleClose={() => setAuthorizedPersonEdit(null)}
        />
      )}
      <BaseTable
        serverSidePaginationConfig={serverSidePaginationConfig}
        paginationType="serverSide"
        columns={columns}
        displaySrNo
        pageSize={RECORDS_PER_PAGE}
        maxHeight="calc(100vh - 200px)"
        registerResetPage={(cb) => {
          resetPageRef.current = cb;
          setIsResetPageRegistered((prev) => !prev);
        }}
      />
    </div>
  );
};

AuthorizedUsers.propTypes = {
  t: PropTypes.func.isRequired,
};

export default withTranslation()(AuthorizedUsers);
